import { defineStore } from 'pinia'
import { PricingSegment } from '@/globals/typescript/models/PricingSegment'
import type {
  IPricingSegment,
  IPricingSegmentTatEntry,
  IPricingSegmentAnalysisEntry,
  IPricingSegmentAddonEntry,
  IPricingSegmentDealTier,
  IPricingSegmentTieredDealEntry,
  IPricingSegmentIntervalDealEntry,
  TPricingSegmentDealEntry,
  TDealType,
} from '@team-milva/lab-models/pricingSegment'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAppStore } from '@/stores/appStore'
import { useAccountStore } from '@/stores/accountStore'
import type { TLabManagementCurrencyCode } from '@team-milva/lab-models/currency'

export { PricingSegment }
export type {
  IPricingSegment,
  IPricingSegmentTatEntry,
  IPricingSegmentAnalysisEntry,
  IPricingSegmentAddonEntry,
  IPricingSegmentDealTier,
  IPricingSegmentTieredDealEntry,
  IPricingSegmentIntervalDealEntry,
  TPricingSegmentDealEntry,
  TDealType,
}

type CreatePricingSegmentParams = {
  name: string
  currencyCode: TLabManagementCurrencyCode
}

type ClonePricingSegmentParams = {
  name: string
  currencyCode: TLabManagementCurrencyCode
}

type UpdatePricingSegmentParams = {
  name?: Record<string, string>
  activeState?: 'active' | 'archived'
  currencyCode?: TLabManagementCurrencyCode
  tats?: IPricingSegmentTatEntry[]
  analyses?: IPricingSegmentAnalysisEntry[]
  addons?: IPricingSegmentAddonEntry[]
  deals?: TPricingSegmentDealEntry[]
}

export const usePricingSegmentStore = defineStore('pricingSegment', {
  state: () => ({
    allPricingSegments: [] as PricingSegment[],
    unsubscribeAllPricingSegments: null as (() => void) | null,
    listStateFilter: 'active' as 'active' | 'archived',
  }),

  getters: {
    allPricingSegmentsLoaded: (state): boolean =>
      !!state.unsubscribeAllPricingSegments,

    allPricingSegmentsAsObject: (state): Record<string, PricingSegment> => {
      const obj: Record<string, PricingSegment> = {}
      for (const segment of state.allPricingSegments) {
        obj[segment.id] = segment
      }
      return obj
    },
  },

  actions: {
    getPricingSegments(): Promise<PricingSegment[]> {
      if (this.allPricingSegmentsLoaded) {
        return Promise.resolve(this.allPricingSegments)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'PricingSegments'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allPricingSegments = querySnapshot.docs.map(
            (doc) => new PricingSegment(doc.data() as IPricingSegment)
          )
          this.unsubscribeAllPricingSegments = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allPricingSegments)
          }
        })
      })
    },

    async createPricingSegment(
      params: CreatePricingSegmentParams
    ): Promise<string> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/pricing-segments',
        {
          path: { labAccountID: accountStore.labAccount?.id ?? '' },
          body: {
            name: { [currentLocale]: params.name },
            // TODO: remove cast after deploying and re-running openapi:gen
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            currencyCode: params.currencyCode as any,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
      return result.data.id
    },

    /**
     * Copies a segment into a new one with a new title and returns the new ID. Passing a currency
     * other than the source's makes lab-cloud reset the prices in the copy (see
     * clonePricingSegmentService) — the caller says so in the UI before it gets here.
     */
    async clonePricingSegment(
      sourceID: string,
      params: ClonePricingSegmentParams
    ): Promise<string> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/pricing-segments/{pricingSegmentID}/clone',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            pricingSegmentID: sourceID,
          },
          body: {
            name: { [currentLocale]: params.name },
            currencyCode: params.currencyCode,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
      return result.data.id
    },

    async updatePricingSegment(
      id: string,
      params: UpdatePricingSegmentParams
    ): Promise<void> {
      const accountStore = useAccountStore()

      const body: {
        name?: Record<string, string>
        activeState?: 'active' | 'archived'
        currencyCode?: TLabManagementCurrencyCode
        tats?: IPricingSegmentTatEntry[]
        analyses?: IPricingSegmentAnalysisEntry[]
        addons?: IPricingSegmentAddonEntry[]
        deals?: TPricingSegmentDealEntry[]
      } = {}

      if ('name' in params) {
        body['name'] = params.name
      }
      if ('activeState' in params) {
        body['activeState'] = params.activeState
      }
      if ('currencyCode' in params) {
        body['currencyCode'] = params.currencyCode
      }
      if ('tats' in params) {
        body['tats'] = params.tats
      }
      if ('analyses' in params) {
        body['analyses'] = params.analyses
      }
      if ('addons' in params) {
        body['addons'] = params.addons
      }
      if ('deals' in params) {
        body['deals'] = params.deals
      }

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/pricing-segments/{pricingSegmentID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            pricingSegmentID: id,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          body: body as any,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    resetStore(): void {
      this.unsubscribeAllPricingSegments?.()
      this.allPricingSegments = []
      this.unsubscribeAllPricingSegments = null
      this.listStateFilter = 'active'
    },
  },
})

